<template>
  <main class="container">
    <h1 class="page-title">Please Confirm Your Address</h1>

    <BackButton class="back-link" />

    <WarningPanel
      v-if="errorResponse"
      title="There's a problem..."
      :msg="errorResponse"
      :severe="true"
    />

    <form
      class="form address-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <div class="form-item">
          <label class="form__label" for="postcode">
            <span class="form__label-text">
              <span class="label">Your postcode</span>
            </span>
          </label>
          <input
            class="form__input"
            id="postcode"
            name="postcode"
            type="text"
            :value="postcode"
            readonly
          />
        </div>
      </fieldset>

      <fieldset class="form__fieldset" v-if="!isManualAddress">
        <div
          class="form-item"
          :class="{ '--has-error': $v.addressGuid.$error }"
        >
          <label class="form__label" for="addressGuid">
            <span class="form__label-text">
              <span class="label">Select your property</span>
              <a href="#" class="help" @click.stop="addressInputTypeToggle">
                Input address manually
              </a>
            </span>
            <FormError
              v-if="
                $v.addressGuid.$error && !$v.addressGuid.requiredIfNotManual
              "
              msg="You must pick an address"
            />
          </label>
          <div class="select-wrapper">
            <select
              class="form__input"
              id="addressGuid"
              name="addressGuid"
              v-model="addressGuid"
            >
              <option disabled value="">Please select your address</option>
              <option
                v-for="address in addressOptions"
                :key="address.guid"
                :value="address.guid"
                >{{ concatAddress(address) }}</option
              >
            </select>
          </div>
        </div>
      </fieldset>

      <fieldset class="form__fieldset" v-else>
        <div
          class="form-item"
          :class="{ '--has-error': $v.fullAddress.line1.$error }"
        >
          <label class="form__label" for="addressLine1">
            <span class="form__label-text">
              <span class="label">Address line 1</span>
              <a href="#" class="help" @click.stop="addressInputTypeToggle">
                Pick address from list
              </a>
            </span>
            <FormError
              v-if="
                $v.fullAddress.line1.$error && !$v.fullAddress.line1.required
              "
              msg="Address line 1 required"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="addressLine1"
            name="addressLine1"
            required
            autocomplete="address-line1"
            v-model.trim="fullAddress.line1"
          />
        </div>

        <div class="form-item">
          <label class="form__label" for="addressLine2">
            <span class="form__label-text">Address line 2 (optional)</span>
          </label>
          <input
            class="form__input"
            type="text"
            id="addressLine2"
            name="addressLine2"
            autocomplete="address-line2"
            v-model.trim="fullAddress.line2"
          />
        </div>

        <div
          class="form-item"
          :class="{ '--has-error': $v.fullAddress.city.$error }"
        >
          <label class="form__label" for="city">
            <span class="form__label-text">City</span>
            <FormError
              v-if="$v.fullAddress.city.$error && !$v.fullAddress.city.required"
              msg="City/Town required"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="city"
            name="city"
            required
            autocomplete="address-level2"
            v-model.trim="fullAddress.city"
          />
        </div>
      </fieldset>

      <button
        class="btn btn--big btn--full-width form__submit"
        type="submit"
        @click="trackSubmitBtn"
      >
        Next
      </button>
    </form>
  </main>
</template>

<script>
import { requiredIf } from "vuelidate/lib/validators";
import { mapGetters, mapMutations } from "vuex";
import axiosInstance from "@/utility/axiosInstance";
import FormError from "@/components/forms/FormError.vue";
import WarningPanel from "@/components/WarningPanel";

export default {
  name: "AddressPicker",
  data() {
    return {
      errorResponse: "",
      addressOptions: [],
      addressGuid: "",
      fullAddress: {
        line1: "",
        line2: "",
        city: "",
      },
    };
  },
  computed: {
    ...mapGetters(["postcode"]),
    isManualAddress: {
      get() {
        return this.$store.state.uiState.isManualAddress;
      },
      set(value) {
        this.$store.commit("SET_ADDRESS_INPUT_TYPE", value);
      },
    },
  },
  methods: {
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
      showBackBtn: "SHOW_BACK_BTN",
    }),
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        if (!this.isManualAddress) {
          this.showLoadingPanel();

          const selectedAddress = this.addressOptions.find(
            (address) => address.guid === this.addressGuid,
          );

          axiosInstance
            .post(`/verify_address/${this.addressGuid}`)
            .then(({ data }) => {
              // if we find a matching account for this guid,
              // redirect to the "account already exists screen"
              this.setLeadTenantAddress(selectedAddress);

              this.$router.push({
                name: "account-already-exists",
                params: { guid: data.shareCode },
              });
            })
            .catch(() => {
              // error probably means we didn't find any matching address,
              // or that the API was down, this is not a strict check
              // so proceed as normal
              this.setLeadTenantAddress(selectedAddress);
              this.$router.push("/your-details");
            })
            .finally(() => {
              this.hideLoadingPanel();
            });
        } else {
          this.setLeadTenantAddress({
            fullAddress: this.fullAddress,
          });
          this.$router.push("/your-details");
        }
      }
    },
    concatAddress(address) {
      let fullAddress = [];

      if (address.line1) {
        fullAddress.push(address.line1);
      }

      if (address.line2) {
        fullAddress.push(address.line2);
      }

      return fullAddress.join(", ");
    },
    addressInputTypeToggle() {
      this.trackGoogleEvent(
        !this.isManualAddress
          ? "Click Enter Address Manually"
          : "Click Pick address from list",
        "Create Account",
        !this.isManualAddress
          ? "Enter address manually"
          : "Pick address from list",
      );

      this.isManualAddress = !this.isManualAddress;
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Let's Go",
        "Create Account",
        "Confirm address",
      );
    },
  },
  created() {
    this.showLoadingPanel();

    axiosInstance
      .get(`/addresses/lookup/${this.postcode}`)
      .then(({ data }) => {
        this.addressOptions = data;
      })
      .catch((error) => {
        if (error.response.data.messages) {
          this.errorResponse = error.response.data.messages[0];
        } else {
          this.errorResponse = "Failed to fetch addresses";
        }
      })
      .finally(() => {
        this.hideLoadingPanel();
      });

    // ensure the back button is visible.
    // It's possible you'll see the "house already signed up"
    // screen which hides the back button, and then click
    // the browse back button to this page
    this.showBackBtn();

    // check to see if we already have the address and overwrite local data if so.
    const addressGuid = this.$store.getters.addressGuid;
    const fullAddress = this.$store.getters.fullAddress;

    if (addressGuid) {
      this.addressGuid = addressGuid;
    } else if (fullAddress) {
      this.fullAddress = fullAddress;
      this.isManualAddress = true;
    }
  },
  validations() {
    return {
      addressGuid: {
        required: requiredIf(() => !this.isManualAddress),
      },
      fullAddress: {
        line1: {
          required: requiredIf(() => this.isManualAddress),
        },
        city: {
          required: requiredIf(() => this.isManualAddress),
        },
      },
    };
  },
  components: {
    FormError,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.form__submit {
  display: block;
  margin: auto auto 4rem;
}

.form__label-text {
  .label {
    flex-grow: 1;
  }

  .help {
    text-decoration: underline;
    cursor: pointer;
    align-self: flex-end;
    color: $brand-dark-blue;
  }
}

.select-wrapper {
  position: relative;

  &:before {
    content: "";
    display: block;
    width: 10px;
    height: 10px;

    border-bottom: 2px solid $grey-mid;
    border-right: 2px solid $grey-mid;

    position: absolute;
    top: calc(50% - 10px);
    right: $spacing-medium;

    transform: rotateZ(45deg);
  }

  select {
    padding-right: $spacing-small * 3;
  }
}
</style>
